<template>
  <div class="main-about-us-container">
    <div class="full-page-layout lazyloaded">
      <div class="container">
        <div>
          <h1 class="h2">
            {{ $c.text('pages.about-us.title') }}
          </h1>
          <div class="col-8 subtitle">
            {{ $c.text('pages.about-us.description') }}
          </div>
          <nuxt-link to="/signup/" class="col-4 md-3 btn-primary btn-lg">
            {{ $c.text($config.platform === 'ds' ? 'button.start-building-your-career' : 'button.get-started') }}
          </nuxt-link>
        </div>
        <div class="logos">
          <p class="body-2">
            {{ $c.text('pages.about-us.publications') }}
          </p>
          <img v-if="$config.platform === 'ds'" src="../assets/images/about-us-companies.png" alt="logos">
          <div v-else class="logos-wrap">
            <LogoForbes />
            <LogoInc />
            <LogoHuff />
            <LogoBusinessInsider />
            <LogoTheEconomist />
          </div>
        </div>
      </div>
    </div>
    <div class="container platform-aim">
      <div class="row">
        <div class="col-5 col">
          <h2>{{ $c.text('pages.about-us.infoTitle') }}</h2>
        </div>
        <div class="col-7 col">
          <div class="body-1">
            {{ $c.text('pages.about-us.infoDescription') }}
          </div>
        </div>
      </div>
    </div>
    <Lazy never>
      <div class="container">
        <div class="our-vision-container">
          <div class="our-vision">
            <img loading="lazy" :src="getImg">
            <div class="col-8 sm-12 main-gradient">
              <h2>{{ $c.text('pages.about-us.ourVision') }}</h2>
              <div class="box-row">
                <div class="col-6 box">
                  <h3 class="h4">
                    {{ $c.text('pages.about-us.vision1.title') }}
                  </h3>
                  <p class="body-2">
                    {{ $c.text('pages.about-us.vision1.description') }}
                  </p>
                </div>
                <div class="col-6 box">
                  <h3 class="h4">
                    {{ $c.text('pages.about-us.vision2.title') }}
                  </h3>
                  <p class="body-2">
                    {{ $c.text('pages.about-us.vision2.description') }}
                  </p>
                </div>
              </div>
              <div class="box-row">
                <div class="col-6 box">
                  <h3 class="h4">
                    {{ $c.text('pages.about-us.vision3.title') }}
                  </h3>
                  <p class="body-2">
                    {{ $c.text('pages.about-us.vision3.description') }}
                  </p>
                </div>
                <div class="col-6 box">
                  <h3 class="h4">
                    {{ $c.text('pages.about-us.vision4.title') }}
                  </h3>
                  <p class="body-2">
                    {{ $c.text('pages.about-us.vision4.description') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Lazy>
    <Lazy :when-visible="{ rootMargin: '200px' }">
      <div class="container">
        <div class="students-container">
          <div class="row">
            <div class="left col col-5">
              <h3>{{ $c.text('pages.about-us.stats.title') }}</h3>
              <div class="col-12">
                <div class="number-box">
                  <span class="number">{{ stats.rounded.videoDuration }}</span>
                  <p class="body-1">
                    Hours of video
                  </p>
                </div>
                <div class="number-box">
                  <span class="number">{{ stats.rounded.lectures.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") }}</span>
                  <p class="body-1">
                    Lessons
                  </p>
                </div>
              </div>
              <div class="col-12">
                <div class="number-box">
                  <span class="number">{{ stats.rounded.courses }}</span>
                  <p class="body-1">
                    Courses
                  </p>
                </div>
                <div class="number-box">
                  <span class="number">2M+</span>
                  <p class="body-1">
                    Students
                  </p>
                </div>
              </div>
            </div>
            <div class="right col col-6 sm-7 offset-1">
              <Animation :options="{ data: $config.platform === 'fa' ? 'fa/fa-365-students' : '365-students' }" />
            </div>
          </div>
        </div>
      </div>
    </Lazy>
    <div v-if="team.length" class="team-members">
      <Lazy :when-visible="{ rootMargin: '200px' }">
        <div class="container">
          <div class="row team-desc centered">
            <div class="col-8 md-9 col">
              <h2 class="h2">
                {{ $c.text('pages.about-us.meetTheTeam.title') }}
              </h2>
              <div class="body-1" v-html="$c.text('pages.about-us.meetTheTeam')" />
            </div>
          </div>
          <div class="row">
            <template
              v-for="(member, i) in team"
            >
              <div
                v-if="!member.hidden"
                :key="i" class="single-member sm-4 col"
                :class="[{ 'hide-for-phone': i > 8 && showMore !== true }, 'col-4', 'md-4']"
              >
                <div class="front-cover">
                  <img
                    loading="lazy"
                    class="member-image"
                    :src="`${$config.imagesUrl}/team/${member.image}`"
                    :alt="member.name"
                  >
                  <span class="member-name h4">{{ member.name }}</span> <br>
                  <span class="member-title body-2">{{ member.title }}</span>
                </div>
              </div>
            </template>
            <div
              class="see-more"
              :class="{ collapsed: showMore }"
              @click="showMore = !showMore"
            >
              <span
                v-if="!showMore"
              >
                See More <IconArrowDown />
              </span>
              <span
                v-else
              >
                See Less <IconArrowDown />
              </span>
            </div>
          </div>
        </div>
      </Lazy>
    </div>
    <Lazy never>
      <PreFooter
        :title="$c.text('pages.about-us.prefooter.title')"
        :description="$c.text('pages.about-us.prefooter.description')"
        :button-title="$c.text($config.platform === 'ds' ? 'button.start-your-career' : 'button.get-started')"
        button-link="/signup/"
        :columns="'col-8'"
      />
    </Lazy>
  </div>
</template>

<script>
import Lazy from 'vue-lazy-hydration';
import global from '@itbpbg/lms-components/src/mixins/global';
import Animation from '../components/Animation';
import IconArrowDown from '../assets/svg/icon-expand.svg?vue-component';
import PreFooter from '../components/PreFooter';
import LogoForbes from '../assets/svg/logos/forbes.svg?vue-component';
import LogoTheEconomist from '../assets/svg/logos/the-economist.svg?vue-component';
import LogoBusinessInsider from '../assets/svg/logos/business-insider.svg?vue-component';
import LogoHuff from '../assets/svg/logos/huffpost.svg?vue-component';
import LogoInc from '../assets/svg/logos/inc.svg?vue-component';

export default {
  components: {
    Lazy,
    Animation,
    PreFooter,
    IconArrowDown,
    LogoForbes,
    LogoTheEconomist,
    LogoBusinessInsider,
    LogoHuff,
    LogoInc,
  },
  mixins: [global],
  async asyncData({ $axios, route, $config }) {
    const [team, page, stats] = await Promise.all([
      $axios.$get('/team/members'),
      $axios.$get(`/pages/${route.fullPath}`),
      $axios.$get('/stats/courses?reduced=1') || { rounded: {}, raw: {} },
    ]);
    page.fullPath = `${$config.siteUrl}${route.fullPath}`;
    page.metaFullImageUrl = `${$config.siteUrl}/resources/${page.metaImage}`;

    page.created = page.created || new Date();
    page.updated = page.updated || new Date();

    return {
      team,
      page,
      stats,
    };
  },
  data: () => ({
    showMore: false,
  }),
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps(this.page),
      script: [linkedData],
    };
  },
  computed: {
    getImg() {
      if (this.$config.platform === 'fa') {
        return require('../assets/images/fa/fa-about-us-our-vision.jpg');
      }
      return require('../assets/images/about-us-our-vision.png');
    },
  },
};
</script>

<style scoped lang="scss">
.full-page-layout {
  background-image: url('../assets/images/about-us.jpg');
  image-rendering: -webkit-optimize-contrast;
  background-size: cover;
  background-position: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 660px;

  .h2 {
    text-transform: capitalize;
  }

  .row {
    grid-row-gap: 0;
  }

  div {
    color: #FFF;
    text-align: center;

    h1,
    p {
      margin: 0;
    }

    .subtitle {
      color: var(--main-light-shade-4);
      margin: var(--spacing-16) auto var(--spacing-48);

      @include bp($bp-mobile) {
        width: 100%;
        padding-left: var(--spacing-12);
        padding-right: var(--spacing-12);
      }
    }

    .btn-lg {
      width: 100%;
    }

    .logos {
      margin-top: 71px;

      .body-2 {
        color: var(--main-light-shade-4);
        margin-bottom: var(--spacing-20);
      }
    }
  }

  @include bp($bp-phone) {
    min-height: 100vh;
    padding: var(--spacing-80) 0;

    > div {
      padding-top: var(--spacing-80);
    }

    div {
      .logos {
        display: none;
      }
    }
  }
}

.platform-aim {
  padding-top: 135px;
  padding-bottom: 160px;

  h2 {
    margin-top: 0;
  }

  @include bp($bp-phone) {
    padding: var(--spacing-80) var(--spacing-24);
  }
}

.our-vision-container {
  .our-vision {
    border-radius: 10px;
    color: var(--main-light);
    position: relative;
    overflow: hidden;

    img {
      position: absolute;
      right: 0;
      height: 100%;
      z-index: -1;
    }

    .main-gradient {
      padding: 49px var(--spacing-100);
      background: linear-gradient(90deg, rgba(0, 12, 31, 0.94) 0%, var(--main-dark) 0.01%, rgba(0, 12, 31, 0.9) 53.13%, rgba(0, 12, 31, 0) 100%);
    }

    h2 {
      margin: 0 0 var(--spacing-6) 0;
    }

    .box-row {
      display: flex;

      .box {
        margin: var(--spacing-30) 0 0;
        padding-right: var(--spacing-20);

        &:first-child {
          margin-right: var(--spacing-10);
        }

        h3 {
          margin: 0;
          margin-bottom: 8px;
        }

        .body-2 {
          margin: 0;
        }
      }

      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }

  @include bp($bp-phone) {
    padding: 0;
    width: calc(100% + 48px);
    margin-left: -24px;

    .our-vision {
      background-position: right bottom;
      background-size: auto 450px;
      padding-bottom: 450px;
      border-radius: 0;

      img {
        height: 450px;
        bottom: 0;
      }

      h2 {
        margin: 0;
      }

      .main-gradient {
        background: var(--main-dark);
        padding: var(--spacing-80) var(--spacing-24);

        &:before,
        &:after {
          content: '';
          left: 0;
          position: absolute;
          width: 100%;
        }

        &:before {
          background: linear-gradient(180deg, var(--main-dark) 0%, rgba(0, 12, 31, 0.76) 37.5%, rgba(0, 12, 31, 0) 100%);
          height: 100px;
          bottom: 350px;
        }

        &:after {
          bottom: 0;
          background: linear-gradient(180deg, var(--main-dark) 0%, rgba(0, 12, 31, 0) 100%);
          height: 150px;
          transform: rotate(-180deg);
        }
      }

      .box-row {
        display: inline-block;

        .box {
          margin: var(--spacing-36) 0 0;
          width: 100%;
          margin-right: 0;
        }
      }
    }
  }
}

.students-container {
  padding: var(--spacing-160) 0 var(--spacing-100);

  h3 {
    margin: 0 0 var(--spacing-60) 0;
    width: 99%;
  }

  .row {
    align-items: center;

    .left {
      .number-box {
        margin-bottom: 50px;
        max-width: 200px;
        width: 50%;
        flex: 0 0 50%;
        float: left;
      }

      .number {
        font-size: 57px;
        line-height: 1.1;
      }

      p.body-1 {
        margin: 0;
        color: var(--main-dark-shade-3);
      }
    }
  }

  @include bp($bp-phone) {
    padding: var(--spacing-80) 0;

    h3 {
      margin-bottom: var(--spacing-36);
    }

    .row {
      .left {
        .number {
          font-size: 44px;
          line-height: 1.1;
        }
      }
    }
  }
}

.team-members {
  background: var(--gradient-brand);
  background-color: var(--main-dark);
  color: var(--main-light);
  padding: var(--spacing-120) 0;

  .team-desc {
    text-align: center;

    h1 {
      margin: 0;
      margin-bottom: var(--spacing-16);
    }
  }

  .single-member {
    padding-bottom: var(--spacing-24);
    padding-top: var(--spacing-36);

    .front-cover {
      margin: auto;

      .member-image {
        background-color: #112030;
        border-radius: 10px;
        margin-bottom: var(--spacing-24);
        height: 461px;

        object-fit: cover;
        object-position: center;
        display: block;
        width: 100%;

        .member-title {
          white-space: break-spaces;
        }
      }
    }
  }

  .see-more {
    display: none;

    &.collapsed {
      svg {
        transform: rotate(180deg);
      }
    }
  }

  @include bp($bp-phone) {
    padding: var(--spacing-80) 0;

    .see-more {
      display: table;
      text-align: center;
      color: var(--accent-shade-1);
      font-size: 16px;
      font-weight: 600;
      margin: auto;

      svg {
        path {
          fill: var(--accent-shade-1);
        }
      }
    }

    .hide-for-phone {
      display: none;
    }
  }
}

.fa {
  .full-page-layout {
    background-image: url("../assets/images/fa/fa-about-us.jpg");

    .logos-wrap {
      svg:not(:last-of-type) {
        margin-right: 50px;
      }
    }
  }

  .team-members {
    background: var(--main-dark);

    .single-member .front-cover {
      max-width: 100%;
    }
  }

  .our-vision-container .our-vision .main-gradient {
    background: linear-gradient(90deg, var(--main-dark) 0%, var(--main-dark) 38.43%, rgba(1, 22, 51, 0.9) 57.09%, rgba(1, 22, 51, 0) 100%);
  }
}

@include bp($bp-laptop) {
  .our-vision-container {
    .our-vision {
      .main-gradient {
        padding: 74px 84px;
      }

      .box-row {
        .box {
          padding-right: var(--spacing-20);

          &:first-child {
            margin-right: 10px;
          }

          .body-2 {
            font-size: 14px;
          }
        }
      }
    }
  }

  .team-members {
    .team-desc {
      > div {
        padding: 0;
      }
    }

    .single-member {
      .front-cover {
        .member-image {
          height: 400px;
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .fa {
    .full-page-layout {
      background-image: url('../assets/images/fa/fa-about-us-2.jpg');
      background-position: top;
      background-repeat: no-repeat;
      background-color: #011633;
      background-size: 100% 100%;

      .h2 {
        font-size: 32px;
      }

      div .subtitle {
        line-height: 150%;
        margin: var(--spacing-16) 0 var(--spacing-24);
      }

      div .logos {
        display: block;
        margin-bottom: 0;
        padding-top: var(--spacing-40);
      }

      .logos-wrap {
        flex-direction: column;
        display: flex;
        align-items: center;

        svg:not(:last-of-type) {
          margin-right: 0;
          margin-bottom: 50px;
        }
      }
    }

    .our-vision-container .our-vision .main-gradient {
      background: var(--main-dark);
    }

    .team-members {
      .team-desc {
        margin: 0;

        .offset-2 {
          text-align: center;
          max-width: 327px;
          margin: 0 auto;
        }
      }

      .single-member .front-cover {
        max-width: 240px;

        .member-image {
          height: 300px;
        }
      }
    }
  }
}
</style>
